import { useEffect, useMemo, useRef, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import type { PreviewTake, StageStateV1 } from '@shotstead/contracts'
import { validateStageStateV1 } from '@shotstead/contracts'
import { StageScene, rehearsalDurationSeconds } from '@shotstead/stage-runtime-three'
import { capturePreviewTakeFromCanvas } from './runtime/capturePreview'
import { useRehearsalClock, type RehearsalClock } from './runtime/useRehearsalClock'

export type BrowserRehearsalReceipt = {
  execution_id: string
  stage_id: string
  stage_version: number
  status: 'completed' | 'failed'
  warnings: []
}

export type ShotsteadAgentRuntimeBridge = {
  startRehearsal(stage: StageStateV1): Promise<BrowserRehearsalReceipt>
  capturePreviewTake(stage: StageStateV1): Promise<PreviewTake>
}

declare global {
  interface Window {
    __SHOTSTEAD_AGENT_RUNTIME_BRIDGE__?: ShotsteadAgentRuntimeBridge
  }
}

type CaptureView = {
  objectUrl: string
  byteSize: number
  take: PreviewTake
}

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds))

async function waitUntil(
  predicate: () => boolean,
  timeoutMilliseconds: number,
  message: string,
): Promise<void> {
  const startedAt = performance.now()
  while (!predicate()) {
    if (performance.now() - startedAt > timeoutMilliseconds) {
      throw new Error(message)
    }
    await wait(25)
  }
}

function assertExecutableStage(stage: StageStateV1): StageStateV1 {
  const validation = validateStageStateV1(stage)
  if (!validation.valid) {
    throw new Error(`Browser Runtime rejected invalid Stage: ${validation.errors.join('; ')}`)
  }
  return structuredClone(stage)
}

export function AgentRuntimeHarness() {
  const [stage, setStage] = useState<StageStateV1 | null>(null)
  const [runtimeStatus, setRuntimeStatus] = useState('Awaiting Stage Coordinator Runtime package')
  const [lastCapture, setLastCapture] = useState<CaptureView | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const canvasReadyRef = useRef(false)
  const renderedStageRef = useRef<{ stage_id: string; stage_version: number } | null>(null)
  const executionQueueRef = useRef<Promise<void>>(Promise.resolve())
  const lastObjectUrlRef = useRef<string | null>(null)

  const durationSeconds = useMemo(() => (stage ? rehearsalDurationSeconds(stage) : 1), [stage])
  const clock = useRehearsalClock(durationSeconds)
  const clockRef = useRef<RehearsalClock>(clock)
  clockRef.current = clock

  useEffect(() => {
    renderedStageRef.current = stage
      ? { stage_id: stage.stage_id, stage_version: stage.stage_version }
      : null
  }, [stage])

  useEffect(() => {
    return () => {
      if (lastObjectUrlRef.current) URL.revokeObjectURL(lastObjectUrlRef.current)
    }
  }, [])

  useEffect(() => {
    const loadStage = async (input: StageStateV1) => {
      const executableStage = assertExecutableStage(input)
      setStage(executableStage)
      setRuntimeStatus(`Loading Stage ${executableStage.stage_id} v${executableStage.stage_version} into browser Runtime…`)

      await waitUntil(
        () =>
          canvasReadyRef.current &&
          renderedStageRef.current?.stage_id === executableStage.stage_id &&
          renderedStageRef.current.stage_version === executableStage.stage_version,
        5_000,
        `Browser Runtime did not render Stage ${executableStage.stage_id} v${executableStage.stage_version} in time.`,
      )

      // Give React Three Fiber one additional frame after the Stage prop commit so
      // capture/rehearsal starts from the exact package that crossed the bridge.
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
      return executableStage
    }

    const enqueue = <T,>(operation: () => Promise<T>): Promise<T> => {
      const next = executionQueueRef.current.then(operation, operation)
      executionQueueRef.current = next.then(
        () => undefined,
        () => undefined,
      )
      return next
    }

    const bridge: ShotsteadAgentRuntimeBridge = {
      startRehearsal: (input) =>
        enqueue(async () => {
          const executableStage = await loadStage(input)
          const expectedDuration = rehearsalDurationSeconds(executableStage)
          setRuntimeStatus(
            `Rehearsing Stage ${executableStage.stage_id} v${executableStage.stage_version} in Chromium…`,
          )
          clockRef.current.replay()

          await waitUntil(
            () =>
              !clockRef.current.isPlaying &&
              clockRef.current.elapsedSeconds >= expectedDuration - 0.02,
            Math.ceil(expectedDuration * 1_000) + 3_000,
            `Browser rehearsal did not complete Stage v${executableStage.stage_version} in time.`,
          )

          const receipt: BrowserRehearsalReceipt = {
            execution_id: crypto.randomUUID(),
            stage_id: executableStage.stage_id,
            stage_version: executableStage.stage_version,
            status: 'completed',
            warnings: [],
          }
          setRuntimeStatus(
            `Rehearsal complete · Stage ${receipt.stage_id} v${receipt.stage_version}`,
          )
          return receipt
        }),
      capturePreviewTake: (input) =>
        enqueue(async () => {
          const executableStage = await loadStage(input)
          const canvas = canvasRef.current
          if (!canvas || !canvasReadyRef.current) {
            throw new Error('Browser Runtime Viewfinder canvas is not ready for capture.')
          }

          const expectedDuration = rehearsalDurationSeconds(executableStage)
          setRuntimeStatus(
            `Capturing real Preview Take · Stage ${executableStage.stage_id} v${executableStage.stage_version}…`,
          )
          clockRef.current.replay()

          const result = await capturePreviewTakeFromCanvas({
            canvas,
            stage: executableStage,
            durationSeconds: expectedDuration,
          })

          if (lastObjectUrlRef.current) URL.revokeObjectURL(lastObjectUrlRef.current)
          lastObjectUrlRef.current = result.objectUrl
          setLastCapture({
            objectUrl: result.objectUrl,
            byteSize: result.blob.size,
            take: result.take,
          })
          setRuntimeStatus(
            `Captured ${result.blob.size.toLocaleString()} bytes · Take ${result.take.take_id} · Stage v${result.take.stage_version}`,
          )
          return result.take
        }),
    }

    window.__SHOTSTEAD_AGENT_RUNTIME_BRIDGE__ = bridge
    return () => {
      if (window.__SHOTSTEAD_AGENT_RUNTIME_BRIDGE__ === bridge) {
        delete window.__SHOTSTEAD_AGENT_RUNTIME_BRIDGE__
      }
    }
  }, [])

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <span className="eyebrow">Shotstead - agent browser executor</span>
          <h1>Agent Runtime Bridge</h1>
        </div>
        <div className="status-stack">
          <span className={`status-dot ${stage ? 'ok' : ''}`}>
            {stage ? `Runtime loaded v${stage.stage_version}` : 'Runtime idle'}
          </span>
          <span className="muted">Stage truth arrives only through the Runtime execution port.</span>
        </div>
      </header>

      <section className="as1-view-grid">
        <article className="viewport-card viewfinder-card">
          <div className="viewport-label">
            <span>Agent-controlled Viewfinder</span>
            <small>Exact committed Stage package</small>
          </div>
          <div className="canvas-wrap viewfinder-wrap">
            {stage ? (
              <Canvas
                shadows
                dpr={[1, 1.5]}
                gl={{ preserveDrawingBuffer: true, antialias: true }}
                onCreated={({ gl }) => {
                  canvasRef.current = gl.domElement
                  canvasReadyRef.current = true
                }}
              >
                <StageScene stage={stage} elapsedSeconds={clock.elapsedSeconds} />
              </Canvas>
            ) : (
              <div className="take-placeholder">Awaiting a committed Stage Runtime package.</div>
            )}
            <div className="frame-lines" aria-hidden="true" />
          </div>
        </article>

        <section className="panel" data-testid="agent-runtime-state">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Execution projection</span>
              <h2>Browser Runtime state</h2>
            </div>
            <span className="timecode">
              {clock.elapsedSeconds.toFixed(2)} / {durationSeconds.toFixed(2)}s
            </span>
          </div>
          <p className="muted" data-testid="agent-runtime-status">{runtimeStatus}</p>
          <dl>
            <div><dt>Stage</dt><dd>{stage ? `${stage.stage_id} v${stage.stage_version}` : 'none'}</dd></div>
            <div><dt>Camera</dt><dd>{stage?.camera.camera_id ?? 'none'}</dd></div>
            <div><dt>Clock</dt><dd>{clock.isPlaying ? 'playing' : 'stopped'}</dd></div>
            <div><dt>Authority</dt><dd>Runtime package projection only</dd></div>
          </dl>
        </section>
      </section>

      <section className="panel take-workflow-panel" data-testid="agent-runtime-capture">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Real browser capture</span>
            <h2>Preview Take evidence</h2>
          </div>
          <span className={`status-dot ${lastCapture ? 'ok' : ''}`}>
            {lastCapture ? 'MediaRecorder capture complete' : 'No capture yet'}
          </span>
        </div>

        {lastCapture ? (
          <div className="take-grid">
            <article className="take-card">
              <div className="take-heading">
                <strong>Agent Preview Take</strong>
                <span className="pill">Stage v{lastCapture.take.stage_version}</span>
              </div>
              <video src={lastCapture.objectUrl} controls playsInline />
              <dl>
                <div><dt>Take</dt><dd>{lastCapture.take.take_id}</dd></div>
                <div><dt>Stage</dt><dd>{lastCapture.take.stage_id} v{lastCapture.take.stage_version}</dd></div>
                <div><dt>Runtime</dt><dd>{lastCapture.take.runtime_adapter}@{lastCapture.take.runtime_version}</dd></div>
                <div><dt>Bytes</dt><dd>{lastCapture.byteSize.toLocaleString()}</dd></div>
              </dl>
            </article>
          </div>
        ) : (
          <div className="take-placeholder">The Stage Coordinator has not captured a Preview Take yet.</div>
        )}
      </section>
    </main>
  )
}
